import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, AlertCircle, Info, Edit2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { updateAllStudentsData } from '@/utils/updateStudentData';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

interface UpdateProgress {
  currentStudent: string;
  progress: number;
  totalStudents: number;
  currentOperation: string;
  incompleteStudents?: { id: string; email: string }[];
}

interface StudentFormData {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  postcode: string;
  gearbox: string;
  licenseNumber: string;
  emergencyContact: string;
  emergencyPhone: string;
  medicalConditions: boolean;
  medicalDetails: string;
  nervousAboutDriving: boolean;
  specificGoals: string;
  focusAreas: string;
  drivingExperience: string;
  vehicleType: string;
  licenseType: string;
  previousLessons: string;
}

const UpdateStudentData = () => {
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get('studentId');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<{ id: string; email: string } | null>(null);
  const [formData, setFormData] = useState<StudentFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    postcode: '',
    gearbox: '',
    licenseNumber: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalConditions: false,
    medicalDetails: '',
    nervousAboutDriving: false,
    specificGoals: '',
    focusAreas: '',
    drivingExperience: '',
    vehicleType: '',
    licenseType: '',
    previousLessons: '0'
  });
  const { toast } = useToast();

  useEffect(() => {
    const fetchStudentData = async () => {
      if (studentId) {
        try {
          const studentDoc = await getDoc(doc(db, 'users', studentId));
          if (studentDoc.exists()) {
            const studentData = studentDoc.data();
            setSelectedStudent({
              id: studentId,
              email: studentData.email || ''
            });
            setFormData({
              firstName: studentData.firstName || '',
              lastName: studentData.lastName || '',
              phone: studentData.phone || '',
              address: studentData.address || '',
              postcode: studentData.postcode || '',
              gearbox: studentData.gearbox || '',
              licenseNumber: studentData.licenseNumber || '',
              emergencyContact: studentData.emergencyContact || '',
              emergencyPhone: studentData.emergencyPhone || '',
              medicalConditions: studentData.medicalConditions || false,
              medicalDetails: studentData.medicalDetails || '',
              nervousAboutDriving: studentData.nervousAboutDriving || false,
              specificGoals: studentData.specificGoals || '',
              focusAreas: studentData.focusAreas || '',
              drivingExperience: studentData.drivingExperience || '',
              vehicleType: studentData.vehicleType || '',
              licenseType: studentData.licenseType || '',
              previousLessons: studentData.previousLessons || '0'
            });
          }
        } catch (error) {
          console.error('Error fetching student data:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch student information',
            variant: 'destructive',
          });
        }
      }
    };

    fetchStudentData();
  }, [studentId, toast]);

  const handleUpdate = async () => {
    setLoading(true);
    setCompleted(false);
    setProgress(null);
    
    try {
      await updateAllStudentsData((progress) => {
        setProgress(progress);
        if (progress.progress === 100) {
          setCompleted(true);
        }
      });
      
      toast({
        title: 'Success',
        description: 'Student data has been updated successfully',
      });
    } catch (error) {
      console.error('Error updating student data:', error);
      toast({
        title: 'Error',
        description: 'Failed to update student data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student: { id: string; email: string }) => {
    setSelectedStudent(student);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) return;

    try {
      await updateDoc(doc(db, 'users', selectedStudent.id), {
        ...formData,
        needsUpdate: false
      });

      toast({
        title: 'Success',
        description: 'Student information has been updated successfully',
      });

      // Update the incomplete students list
      if (progress?.incompleteStudents) {
        const updatedIncompleteStudents = progress.incompleteStudents.filter(
          student => student.id !== selectedStudent.id
        );
        setProgress({
          ...progress,
          incompleteStudents: updatedIncompleteStudents
        });
      }

      setSelectedStudent(null);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        postcode: '',
        gearbox: '',
        licenseNumber: '',
        emergencyContact: '',
        emergencyPhone: '',
        medicalConditions: false,
        medicalDetails: '',
        nervousAboutDriving: false,
        specificGoals: '',
        focusAreas: '',
        drivingExperience: '',
        vehicleType: '',
        licenseType: '',
        previousLessons: '0'
      });
    } catch (error) {
      console.error('Error updating student:', error);
      toast({
        title: 'Error',
        description: 'Failed to update student information',
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (operation: string) => {
    switch (operation) {
      case 'Processing student data':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Updating user profile':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Update completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Update Student Data</h1>
        <p className="mt-2 text-gray-600">
          Synchronize student information across all collections in the system.
        </p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Student Data Update</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">
                    This will update student information in the users collection.
                  </p>
                </div>
                <Button
                  onClick={handleUpdate}
                  disabled={loading}
                  className="min-w-[200px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : completed ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Update Complete
                    </>
                  ) : (
                    "Update All Student Data"
                  )}
                </Button>
              </div>

              {progress && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getStatusColor(progress.currentOperation)}>
                        {progress.currentOperation}
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {progress.progress}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {progress.currentStudent}
                    </div>
                  </div>
                  <Progress value={progress.progress} className="h-2" />
                  <div className="text-sm text-gray-600">
                    Processing student {progress.currentStudent} of {progress.totalStudents}
                  </div>
                </div>
              )}

              {progress?.incompleteStudents && progress.incompleteStudents.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Students Needing Manual Update</AlertTitle>
                  <AlertDescription>
                    <div className="mt-2 space-y-2">
                      <p className="text-sm text-gray-600">
                        The following students have incomplete information and need to be updated manually:
                      </p>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {progress.incompleteStudents.map((student) => (
                              <tr key={student.id}>
                                <td className="px-4 py-2 text-sm text-gray-900">{student.email}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{student.id}</td>
                                <td className="px-4 py-2 text-sm">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleStudentSelect(student)}
                                  >
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    Update Info
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {completed && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Update Completed</AlertTitle>
                  <AlertDescription className="text-green-700">
                    All student data has been synchronized across the system.
                    {progress?.incompleteStudents && progress.incompleteStudents.length > 0 && (
                      <p className="mt-2">
                        Note: {progress.incompleteStudents.length} students need manual updates to complete their information.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>

        {selectedStudent && (
          <Card>
            <CardHeader>
              <CardTitle>Update Student Information</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gearbox">Gearbox</Label>
                    <Input
                      id="gearbox"
                      value={formData.gearbox}
                      onChange={(e) => setFormData({ ...formData, gearbox: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">License Number</Label>
                    <Input
                      id="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContact">Emergency Contact</Label>
                    <Input
                      id="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyPhone">Emergency Phone</Label>
                    <Input
                      id="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="medicalDetails">Medical Details</Label>
                    <Textarea
                      id="medicalDetails"
                      value={formData.medicalDetails}
                      onChange={(e) => setFormData({ ...formData, medicalDetails: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="specificGoals">Specific Goals</Label>
                    <Textarea
                      id="specificGoals"
                      value={formData.specificGoals}
                      onChange={(e) => setFormData({ ...formData, specificGoals: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="focusAreas">Focus Areas</Label>
                    <Textarea
                      id="focusAreas"
                      value={formData.focusAreas}
                      onChange={(e) => setFormData({ ...formData, focusAreas: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="drivingExperience">Driving Experience</Label>
                    <Input
                      id="drivingExperience"
                      value={formData.drivingExperience}
                      onChange={(e) => setFormData({ ...formData, drivingExperience: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType">Vehicle Type</Label>
                    <Input
                      id="vehicleType"
                      value={formData.vehicleType}
                      onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="licenseType">License Type</Label>
                    <Input
                      id="licenseType"
                      value={formData.licenseType}
                      onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="previousLessons">Previous Lessons</Label>
                    <Input
                      id="previousLessons"
                      type="number"
                      value={formData.previousLessons}
                      onChange={(e) => setFormData({ ...formData, previousLessons: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="medicalConditions"
                      checked={formData.medicalConditions}
                      onCheckedChange={(checked) => setFormData({ ...formData, medicalConditions: checked })}
                    />
                    <Label htmlFor="medicalConditions">Medical Conditions</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="nervousAboutDriving"
                      checked={formData.nervousAboutDriving}
                      onCheckedChange={(checked) => setFormData({ ...formData, nervousAboutDriving: checked })}
                    />
                    <Label htmlFor="nervousAboutDriving">Nervous About Driving</Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedStudent(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UpdateStudentData; 